import React from "react";
import styled from "styled-components";
import { ChevronRight, Crown, Heart, Package, Ticket, Truck, UserRound, WalletCards } from "lucide-react";
import blackTshirt from "../../images/shop/black-tshirt.png";
import sneakers from "../../images/shop/sneakers.png";
import crossbodyBag from "../../images/shop/crossbody-bag.png";

const Page = styled.main`
  max-width: 1180px;
  margin: 0 auto;
  color: #172033;
`;

const Hero = styled.section`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 24px;
  margin-bottom: 18px;
  padding: 26px 28px;
  border: 1px solid #e4edf8;
  border-radius: 8px;
  background: linear-gradient(135deg, #ffffff 0%, #eef6ff 100%);
  box-shadow: 0 12px 34px rgba(40, 102, 181, 0.07);
`;

const Profile = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  .avatar {
    display: grid;
    place-items: center;
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background: #2f80ed;
    color: #fff;
  }

  h1 {
    margin: 0 0 6px;
    font-size: 28px;
  }

  p {
    margin: 0;
    color: #66758f;
  }
`;

const Grade = styled.div`
  align-self: center;
  padding: 18px;
  border: 1px solid #dce8f8;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.72);

  span {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #2f80ed;
    font-weight: 900;
  }

  strong {
    display: block;
    margin-top: 8px;
    font-size: 22px;
  }
`;

const StatGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 18px;
`;

const StatCard = styled.article`
  padding: 20px;
  border: 1px solid #e3ebf7;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(32, 72, 122, 0.06);

  svg {
    color: #2f80ed;
  }

  span {
    display: block;
    margin: 10px 0 6px;
    color: #66758f;
    font-size: 13px;
  }

  strong {
    color: #172033;
    font-size: 22px;
  }
`;

const Grid = styled.section`
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 18px;
`;

const Panel = styled.section`
  border: 1px solid #e3ebf7;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 16px 36px rgba(32, 72, 122, 0.07);
  overflow: hidden;

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px;
    border-bottom: 1px solid #e3ebf7;
  }

  h2 {
    margin: 0;
    font-size: 19px;
  }

  button {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 0;
    background: transparent;
    color: #2f80ed;
    font-weight: 900;
    cursor: pointer;
  }
`;

const Order = styled.article`
  display: grid;
  grid-template-columns: 76px 1fr auto;
  gap: 14px;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #edf2f8;

  &:last-child {
    border-bottom: 0;
  }

  img {
    width: 76px;
    height: 76px;
    object-fit: contain;
    border: 1px solid #dfe8f4;
    border-radius: 8px;
  }

  strong {
    display: block;
    color: #172033;
  }

  p {
    margin: 5px 0 0;
    color: #66758f;
    font-size: 13px;
  }
`;

const Status = styled.span`
  border-radius: 999px;
  padding: 7px 10px;
  background: #eaf3ff;
  color: #2f70cf;
  font-size: 12px;
  font-weight: 900;
`;

const ShortcutList = styled.div`
  display: grid;
  padding: 10px;
`;

const Shortcut = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 54px;
  padding: 0 12px;
  border: 0;
  border-radius: 7px;
  background: #fff;
  color: #35445c;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    background: #f7fbff;
  }

  span {
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }

  svg {
    color: #2f80ed;
  }
`;

const recentOrders = [
  { id: 1, name: "로고 크루넥 코튼 티셔츠", date: "2026.05.01", price: 890000, status: "배송완료", image: blackTshirt },
  { id: 2, name: "에어 조던 1 로우 블랙 화이트", date: "2026.04.28", price: 129000, status: "배송중", image: sneakers },
  { id: 3, name: "5AC 미니백", date: "2026.04.20", price: 1290000, status: "구매확정", image: crossbodyBag },
];

export default function MyPage() {
  return (
    <Page>
      <Hero>
        <Profile>
          <div className="avatar"><UserRound size={30} /></div>
          <div>
            <h1>안녕하세요, 고객님</h1>
            <p>최근 주문과 혜택을 한 곳에서 확인하세요.</p>
          </div>
        </Profile>
        <Grade>
          <span><Crown size={18} /> MEMBERSHIP</span>
          <strong>Silver 등급</strong>
        </Grade>
      </Hero>

      <StatGrid>
        <StatCard><WalletCards size={24} /><span>보유 포인트</span><strong>15,420P</strong></StatCard>
        <StatCard><Ticket size={24} /><span>사용 가능 쿠폰</span><strong>4장</strong></StatCard>
        <StatCard><Truck size={24} /><span>배송 중</span><strong>1건</strong></StatCard>
        <StatCard><Heart size={24} /><span>찜한 상품</span><strong>18개</strong></StatCard>
      </StatGrid>

      <Grid>
        <Panel>
          <header>
            <h2>최근 주문</h2>
            <button type="button">전체보기 <ChevronRight size={15} /></button>
          </header>
          {recentOrders.map((order) => (
            <Order key={order.id}>
              <img src={order.image} alt={order.name} />
              <div>
                <strong>{order.name}</strong>
                <p>{order.date} · ₩ {order.price.toLocaleString("ko-KR")}</p>
              </div>
              <Status>{order.status}</Status>
            </Order>
          ))}
        </Panel>

        <Panel>
          <header><h2>빠른 메뉴</h2></header>
          <ShortcutList>
            <Shortcut><span><Package size={18} /> 주문/배송 조회</span><ChevronRight size={16} /></Shortcut>
            <Shortcut><span><RotateCcwIcon /> 반품/교환 관리</span><ChevronRight size={16} /></Shortcut>
            <Shortcut><span><Ticket size={18} /> 쿠폰함</span><ChevronRight size={16} /></Shortcut>
            <Shortcut><span><WalletCards size={18} /> 포인트 충전</span><ChevronRight size={16} /></Shortcut>
          </ShortcutList>
        </Panel>
      </Grid>
    </Page>
  );
}

function RotateCcwIcon() {
  return <Truck size={18} />;
}
